import { useState } from 'react';
import { DataGrid, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid';
import { Pagination, Stack, Box } from '@mui/material';
import {
  ECellType,
  EDataTableType,
  EFeatureStatus,
  TableField,
  TableHeaders,
} from '../../data/Constants';
import { AppState } from '../../store/AppStore';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  createGridColumns,
  createMarketColumns,
  getGroupingModel,
} from '../Helpers/DataTableHelper';
import { getTogglableColumns } from '../../services/ProcosDataTableHelper';
import { setFeatureRecordsPerPage } from '../../store/states/procos';
import { getLocaleForGrid } from '../../services/Util';
import { ProcosCustomToolBar } from './ProcosCustomToolBar';

import type { IFeatureProcosDataTableProps, IMarket, IProCosDataTableProps } from '../../../types';

/**
 * This component renders the Data Table which returns the MA for Features and Options and Products
 * @param { IProCosDataTableProps} props Row data and Table type
 * @returns {React.JSX.Element} Market association Table for Features and Options
 */

export const ProCosDataTable = (
  props: IProCosDataTableProps | IFeatureProcosDataTableProps
): React.JSX.Element => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const procos = useSelector((state: AppState) => state.procos);
  const isEditMode = procos.isEditMode;
  const marketAssociations = procos[props.tableType].ma;
  const localeForGrid = getLocaleForGrid(i18n.language);

  const [tableKey, setTableKey] = useState(0);

  const productMarkets = useSelector((state: AppState) => state.procos.product.ma?.updatedMarkets);
  let markets = useSelector((state: AppState) => state.procos.markets);

  const commonHeaders = TableHeaders[props.tableType];
  let columns: GridColDef[] = createGridColumns({
    tableHeaders: commonHeaders,
    data: marketAssociations,
    isEditMode,
    procos,
    type: props.tableType,
    dispatch,
  });

  // if procos save batching is in progress then we need saveStatus column to be shown in UI
  // otherwise do not show Save column
  columns =
    procos.isCreateRuleInvoked && !procos.isDataUpdated
      ? columns
      : columns.filter((columnItem) => columnItem.field !== ECellType.SaveStatus);

  let columnGroupingModel: GridColumnGroupingModel = [];

  const defaultColumnVisibilityModel =
    props.tableType === EDataTableType.Features ? { [TableField.type]: false } : {};

  //When it is features table add Market Data as Columns
  if (props.tableType === EDataTableType.Features) {
    markets = markets.filter((market: IMarket) => productMarkets?.includes(market.code));
    const marketColumns = createMarketColumns({
      markets,
      marketAssociations,
      isEditMode,
      procos,
      dispatch,
    });
    if (marketColumns) {
      columns = [...columns, ...marketColumns];
      columnGroupingModel = getGroupingModel(markets);
    }
  }

  //To handle Records Change
  const handleRecordsChange = (event: React.ChangeEvent<unknown>) => {
    const records = event.target.value;
    dispatch(setFeatureRecordsPerPage(records));
    props.handleRecordsChange(records);
    setTableKey(records);
  };

  //To Handle Page Change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    props.handlePageChange(value, props.limit);
    setTableKey(value);
  };

  return (
    <>
      <Box id="procos-data-table" className={props.className}>
        {props?.rows && (
          <DataGrid
            onRowSelectionModelChange={(e) => props.handleCheckRow(e)}
            checkboxSelection={
              isEditMode &&
              props.tableType === EDataTableType.Features &&
              procos.productStatus === EFeatureStatus.Active
            }
            rowSelectionModel={props.rowSelectionModel}
            rowHeight={22}
            columnHeaderHeight={30}
            rows={props?.rows}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            initialState={{
              columns: {
                columnVisibilityModel: defaultColumnVisibilityModel,
              },
            }}
            getRowId={(row) => {
              //To set key to row
              return row['id'];
            }}
            hideFooterPagination
            disableRowSelectionOnClick
            disableColumnMenu
            hideFooter
            showToolbar
            slots={{
              toolbar: ProcosCustomToolBar,
            }}
            slotProps={{
              toolbar: {
                limit: props.limit,
                tableType: props.tableType,
                handleClickActive: props.handleClickActive,
                handleRecordsChange: handleRecordsChange,
              },
              columnsManagement: {
                getTogglableColumns,
              },
            }}
            localeText={localeForGrid.components.MuiDataGrid.defaultProps.localeText}
            key={tableKey}
          />
        )}
      </Box>
      <Stack
        spacing={2}
        className={
          props.tableType === EDataTableType.Features
            ? 'procos-features-pagination'
            : 'procos-pagination'
        }
      >
        <Pagination
          size="small"
          id="procos-pagination"
          color="primary"
          count={props.pageCount}
          page={props.page}
          onChange={handlePageChange}
        />
      </Stack>
    </>
  );
};
